/**
 * Аварийный граф (G3, ADR 0009): EmergencyContext ПОВЕРХ edgeAllows — не
 * переписывание правил. blocked → ребро/узел вне рассмотрения; EVACUATION —
 * лифты запрещены (вес ∞ = исключение); дым — штраф ×N. Мультицелевой Dijkstra
 * от точки старта до БЛИЖАЙШЕГО достижимого выхода/укрытия.
 */

import type { LonLat } from '@app/geo'
import type { Adjacent, BuiltGraph } from './graph'
import { edgeAllows } from './graph'
import type { GraphNode } from './types'

export type EmergencyMode = 'EVACUATION' | 'SHELTER' | 'DRILL'

export interface EmergencyContext {
  readonly mode: EmergencyMode
  readonly blockedNodes: ReadonlySet<string>
  /** Ключ ребра: `${from}>${to}` (в обе стороны блокируется одной записью). */
  readonly blockedEdges: ReadonlySet<string>
  readonly smokeNodes: ReadonlySet<string>
}

const SMOKE_PENALTY = 5
/** Категории узлов-лифтов узнаём по привязанному poi/unit category в venue —
 * здесь по kind узла графа (connector-узлы несут kind='elevator' в RVF). */
const ELEVATOR_KIND = 'elevator'

export function edgeKey(from: string, to: string): string {
  return from < to ? `${from}>${to}` : `${to}>${from}`
}

/** Проходимость в аварийном режиме: базовые правила ⊕ контекст. */
export function emergencyAllows(
  ctx: EmergencyContext,
  fromNode: GraphNode,
  toNode: GraphNode,
  entry: Adjacent,
): boolean {
  if (!edgeAllows(entry.edge, 'foot', entry.reverse)) return false
  if (ctx.blockedNodes.has(fromNode.id) || ctx.blockedNodes.has(toNode.id)) return false
  if (ctx.blockedEdges.has(edgeKey(fromNode.id, toNode.id))) return false
  if (
    ctx.mode === 'EVACUATION' &&
    (fromNode.kind === ELEVATOR_KIND || toNode.kind === ELEVATOR_KIND)
  ) {
    return false
  }
  return true
}

export interface EscapePlan {
  readonly targetNodeId: string
  readonly nodeIds: readonly string[]
  readonly polyline: readonly LonLat[]
  readonly distanceM: number
}

/**
 * Ближайший достижимый target (выход/укрытие): один Dijkstra от старта,
 * остановка на первом финальном узле. Недостижимо всё → null (UI честно
 * говорит «следуйте указаниям персонала»).
 */
export function planEscape(
  graph: BuiltGraph,
  startNodeId: string,
  targetNodeIds: readonly string[],
  ctx: EmergencyContext,
): EscapePlan | null {
  const targets = new Set(targetNodeIds)
  if (targets.size === 0 || !graph.nodes.has(startNodeId)) return null
  const state: DijkstraState = {
    dist: new Map([[startNodeId, 0]]),
    prev: new Map(),
    visited: new Set(),
  }
  while (true) {
    const current = nearestUnvisited(state)
    if (current === null) return null
    if (targets.has(current.id)) return buildPlan(graph, current.id, state.prev, current.dist)
    state.visited.add(current.id)
    relaxNeighbors(graph, ctx, state, current)
  }
}

interface DijkstraState {
  readonly dist: Map<string, number>
  readonly prev: Map<string, string>
  readonly visited: Set<string>
}

/** Площадочный масштаб (сотни узлов) — линейный выбор минимума достаточен. */
function nearestUnvisited(state: DijkstraState): { id: string; dist: number } | null {
  let id: string | null = null
  let best = Infinity
  for (const [candidate, d] of state.dist) {
    if (!state.visited.has(candidate) && d < best) {
      best = d
      id = candidate
    }
  }
  return id === null ? null : { id, dist: best }
}

function relaxNeighbors(
  graph: BuiltGraph,
  ctx: EmergencyContext,
  state: DijkstraState,
  current: { id: string; dist: number },
): void {
  const fromNode = graph.nodes.get(current.id)
  if (!fromNode) return
  for (const entry of graph.adj.get(current.id) ?? []) {
    const toNode = graph.nodes.get(entry.to)
    if (!toNode || state.visited.has(entry.to)) continue
    if (!emergencyAllows(ctx, fromNode, toNode, entry)) continue
    const penalty = ctx.smokeNodes.has(entry.to) ? SMOKE_PENALTY : 1
    const candidate = current.dist + entry.distM * penalty
    if (candidate < (state.dist.get(entry.to) ?? Infinity)) {
      state.dist.set(entry.to, candidate)
      state.prev.set(entry.to, current.id)
    }
  }
}

function buildPlan(
  graph: BuiltGraph,
  target: string,
  prev: ReadonlyMap<string, string>,
  distanceM: number,
): EscapePlan {
  const nodeIds: string[] = [target]
  let cursor = target
  while (prev.has(cursor)) {
    cursor = prev.get(cursor) as string
    nodeIds.unshift(cursor)
  }
  const polyline = nodeIds
    .map((id) => graph.nodes.get(id)?.point)
    .filter((p): p is LonLat => p !== undefined)
  return { targetNodeId: target, nodeIds, polyline, distanceM: Math.round(distanceM) }
}
